<script>
// NotificationPreferences.vue
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import {
  hasPushPermissions,
  requestPushPermissions,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { NOTIFICATION_TYPES } from './constants';

export default {
  components: {
    TableHeaderCell,
    ToggleSwitch,
    CheckBox,
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    return { uiSettings, updateUISettings };
  },
  data() {
    return {
      selectedPushFlags: [],
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
      notificationTypes: NOTIFICATION_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    hasPushAPISupport() {
      return !!('Notification' in window);
    },
    isSLAEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.SLA);
    },
    // Aviso de SLA estourado na tela (SlaMissedToaster). Mora em
    // users.ui_settings e NÃO nas flags de push de propósito: push é banner do
    // sistema operacional, e amarrar os dois faria quem tem push registrado
    // receber dois avisos do mesmo estouro.
    // Ausente = ligado, pra que o deploy não silencie ninguém.
    // simplificação: ui_settings é por USUÁRIO, não por usuário-conta — quem
    // atende em duas contas leva a preferência pras duas; se precisar separar,
    // sobe pra notification_settings com coluna própria.
    slaScreenAlertsEnabled: {
      get() {
        return !this.uiSettings.sla_missed_alerts_disabled;
      },
      set(value) {
        this.updateUISettings({ sla_missed_alerts_disabled: !value });
      },
    },
    filteredNotificationTypes() {
      return this.notificationTypes.filter(notification =>
        this.isSLAEnabled
          ? true
          : ![
              'sla_missed_first_response',
              'sla_missed_next_response',
              'sla_missed_resolution',
            ].includes(notification.value)
      );
    },
  },
  watch: {
    pushFlags(value) {
      this.selectedPushFlags = value;
    },
  },
  mounted() {
    if (hasPushPermissions()) {
      this.getPushSubscription();
    }
    this.$store.dispatch('userNotificationSettings/get');
  },
  methods: {
    checkFlagStatus(flagType) {
      return this.selectedPushFlags.includes(`push_${flagType}`);
    },
    async onRegistrationSuccess() {
      this.hasEnabledPushPermissions = true;

      try {
        const registration = await navigator.serviceWorker.ready;

        await registration.showNotification(
          this.$t(
            'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_ENABLED_SUCCESS_TITLE'
          ),
          {
            body: this.$t(
              'PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH_ENABLED_SUCCESS_BODY'
            ),
            icon: '/brand-assets/logo_thumbnail.svg',
            tag: 'test-notification',
          }
        );

        setTimeout(async () => {
          const notifications = await registration.getNotifications({
            tag: 'test-notification',
          });

          if (notifications.length === 0) {
            useAlert(
              this.$t(
                'PROFILE_SETTINGS.FORM.NOTIFICATIONS.SYSTEM_BLOCKED_WARNING'
              ),
              { duration: 10000 }
            );
          }
        }, 2000);
      } catch (error) {
        useAlert(
          this.$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.SYSTEM_BLOCKED_WARNING'),
          { duration: 10000 }
        );
      }
    },
    onRegistrationError() {
      this.hasEnabledPushPermissions = false;
    },
    onRequestPermissions(value) {
      if (value) {
        requestPushPermissions({
          onSuccess: this.onRegistrationSuccess,
          onError: this.onRegistrationError,
        });
      } else {
        this.disablePushPermissions();
      }
    },
    disablePushPermissions() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            if (subscription) {
              return subscription.unsubscribe();
            }
            return null;
          })
          .finally(() => {
            this.hasEnabledPushPermissions = false;
          })
          .catch(() => {
            // error
          })
      );
    },
    getPushSubscription() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            if (!subscription) {
              this.hasEnabledPushPermissions = false;
            } else {
              this.hasEnabledPushPermissions = true;
            }
          })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error))
      );
    },
    async updateNotificationSettings() {
      try {
        this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: [],
          selectedPushFlags: this.selectedPushFlags,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      }
    },
    handlePushInput(id) {
      this.selectedPushFlags = this.toggleInput(this.selectedPushFlags, id);
      this.updateNotificationSettings();
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        return selected.filter(flag => flag !== current);
      }
      return [...selected, current];
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <!-- Dois grupos, de propósito: o de baixo grava flags push_* em
         notification_settings (banner do navegador, exige inscrição); este
         grava em users.ui_settings e controla o aviso dentro do Cortex.
         Caixinha igual na mesma lista faria um parecer o outro. -->
    <div v-if="isSLAEnabled" class="flex flex-col gap-4">
      <span class="text-sm font-medium normal-case text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.SCREEN_ALERTS_SECTION.TITLE') }}
      </span>
      <div
        class="flex items-center justify-between w-full gap-4 p-4 border border-solid border-n-weak rounded-xl"
      >
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.SCREEN_ALERTS_SECTION.SLA_MISSED') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              $t('PROFILE_SETTINGS.FORM.SCREEN_ALERTS_SECTION.SLA_MISSED_NOTE')
            }}
          </span>
        </div>
        <ToggleSwitch v-model="slaScreenAlertsEnabled" />
      </div>
    </div>

    <!-- Layout for desktop devices -->
    <div class="hidden sm:block">
      <span class="block mb-2 text-sm font-medium normal-case text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
      </span>
      <div
        class="grid content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
      >
        <TableHeaderCell
          :span="9"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`"
        >
          <span class="text-sm font-normal normal-case text-n-slate-11">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="3"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH')}`"
        >
          <div class="flex items-center justify-between gap-1">
            <span
              class="text-sm font-medium normal-case text-n-slate-12 whitespace-nowrap"
            >
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
            </span>
          </div>
        </TableHeaderCell>
      </div>
      <div
        v-for="(notification, index) in filteredNotificationTypes"
        :key="index"
      >
        <div
          class="grid items-center content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
        >
          <div
            class="flex flex-row items-start gap-2 col-span-9 px-0 py-2 text-sm tracking-[0.5] rtl:text-right"
          >
            <span class="text-sm text-n-slate-12">
              {{ $t(notification.label) }}
            </span>
          </div>
          <div
            class="flex items-start gap-2 col-span-3 px-0 text-sm tracking-[0.5] text-left rtl:text-right"
          >
            <CheckBox
              :value="`push_${notification.value}`"
              :is-checked="checkFlagStatus(notification.value)"
              @update="id => handlePushInput(id)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Layout for mobile devices -->
    <div class="flex flex-col gap-6 sm:hidden">
      <div class="flex items-center justify-start gap-2">
        <span class="text-sm font-medium normal-case text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
      </div>

      <div class="flex flex-col gap-4">
        <div
          v-for="(notification, index) in filteredNotificationTypes"
          :key="index"
          class="flex flex-row items-start gap-2"
        >
          <CheckBox
            :id="`push_${notification.value}`"
            :value="`push_${notification.value}`"
            :is-checked="checkFlagStatus(notification.value)"
            @update="handlePushInput"
          />
          <span class="text-sm text-n-slate-12">{{
            $t(notification.label)
          }}</span>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-n-weak rounded-xl"
    >
      <div class="flex flex-row items-center gap-2">
        <fluent-icon
          icon="alert"
          class="flex-shrink-0 text-n-slate-12"
          size="18"
        />
        <span class="text-sm text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION') }}
        </span>
      </div>
      <ToggleSwitch
        v-model="hasEnabledPushPermissions"
        @change="onRequestPermissions"
      />
    </div>
  </div>
</template>
